import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle
} from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  LayoutGrid, Calendar, Users, Target, TrendingUp, Archive,
  Edit2, Eye, Download, RefreshCw, Filter, CheckCircle,
  XCircle, Clock, FileText, BarChart3, Settings, Trash2,
  AlertCircle, ArrowLeft
} from 'lucide-react';
import { useConfirm } from '../../hooks/useConfirm';

const API = process.env.REACT_APP_BACKEND_URL || '';

const GerenciarMatrizes9Box = () => {
  const [matrizes, setMatrizes] = useState([]);
  const [filtro, setFiltro] = useState('todas');
  const [loading, setLoading] = useState(false);
  const [estatisticasGerais, setEstatisticasGerais] = useState(null);
  const [matrizVisualizando, setMatrizVisualizando] = useState(null);
  const [posicoes, setPosicoes] = useState([]);
  const [abaAtiva, setAbaAtiva] = useState('gerenciar');
  const { confirm, ConfirmDialog } = useConfirm();

  const quadrantesConfig = {
    1: { nome: 'Q1: Baixo Desempenho', cor: 'bg-red-100 border-red-300' },
    2: { nome: 'Q2: Desempenho Moderado', cor: 'bg-orange-100 border-orange-300' },
    3: { nome: 'Q3: Alto Desempenho', cor: 'bg-yellow-100 border-yellow-300' },
    4: { nome: 'Q4: Potencial em Desenvolvimento', cor: 'bg-blue-100 border-blue-300' },
    5: { nome: 'Q5: Desempenho Sólido', cor: 'bg-green-100 border-green-300' },
    6: { nome: 'Q6: Alta Performance', cor: 'bg-emerald-100 border-emerald-300' },
    7: { nome: 'Q7: Potencial Elevado', cor: 'bg-purple-100 border-purple-300' },
    8: { nome: 'Q8: Futuro Líder', cor: 'bg-indigo-100 border-indigo-300' },
    9: { nome: 'Q9: Talento Estrela', cor: 'bg-pink-100 border-pink-300' }
  };

  useEffect(() => {
    loadMatrizes();
    loadEstatisticasGerais();
  }, []);

  const loadMatrizes = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/9box/matrizes`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.data.success) {
        setMatrizes(response.data.matrizes || []);
      }
    } catch (error) {
      console.error('Erro ao carregar matrizes:', error);
      toast.error('Erro ao carregar matrizes');
    } finally {
      setLoading(false);
    }
  };

  const loadEstatisticasGerais = async () => {
    try {
      const response = await axios.get(`${API}/api/9box/estatisticas-gerais`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.data.success) {
        setEstatisticasGerais(response.data.estatisticas);
      }
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  };

  const handleVisualizar = async (matriz) => {
    try {
      setLoading(true);
      
      // Buscar posições da matriz
      const response = await axios.get(
        `${API}/api/9box/matriz/${matriz.id}/posicoes`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      if (response.data.success) {
        setPosicoes(response.data.posicoes || []);
        setMatrizVisualizando(matriz);
        setAbaAtiva('visualizar');
      }
    } catch (error) {
      console.error('Erro ao carregar posições:', error);
      toast.error('Erro ao carregar matriz');
    } finally {
      setLoading(false);
    }
  };

  const handleArquivar = async (matrizId) => {
    const ok = await confirm({
      title: 'Arquivar matriz?',
      description: 'Tem certeza que deseja arquivar esta matriz?',
      confirmLabel: 'Arquivar',
      destructive: true,
    });
    if (!ok) return;

    try {
      const response = await axios.post(
        `${API}/api/9box/matriz/${matrizId}/arquivar`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      if (response.data.success) {
        toast.success('Matriz arquivada com sucesso!');
        await loadMatrizes();
        await loadEstatisticasGerais();
      }
    } catch (error) {
      toast.error('Erro ao arquivar matriz');
    }
  };

  const handleReativar = async (matrizId) => {
    try {
      const response = await axios.post(
        `${API}/api/9box/matriz/${matrizId}/reativar`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      if (response.data.success) {
        toast.success('Matriz reativada com sucesso!');
        await loadMatrizes();
        await loadEstatisticasGerais();
      }
    } catch (error) {
      toast.error('Erro ao reativar matriz');
    }
  };

  const handleExcluir = async (matrizId) => {
    const ok = await confirm({
      title: 'Excluir matriz permanentemente?',
      description: '⚠️ Esta ação é IRREVERSÍVEL!\n\nTem certeza que deseja excluir permanentemente esta matriz e todos os seus dados relacionados (posições, sessões, ajustes)?',
      confirmLabel: 'Excluir',
      destructive: true,
    });
    if (!ok) return;

    try {
      const response = await axios.delete(
        `${API}/api/9box/matriz/${matrizId}/excluir`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      if (response.data.success) {
        toast.success('Matriz excluída com sucesso!');
        await loadMatrizes();
        await loadEstatisticasGerais();
      }
    } catch (error) {
      toast.error('Erro ao excluir matriz');
    }
  };

  const handleExportar = async (matrizId) => {
    try {
      toast.info('Gerando relatório...');
      const response = await axios.get(
        `${API}/api/9box/matriz/${matrizId}/exportar`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `matriz_9box_${matrizId}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success('Relatório exportado!');
    } catch (error) {
      toast.error('Erro ao exportar matriz');
    }
  };

  const matrizesParaExibir = matrizes.filter(m => {
    if (filtro === 'ativas') return m.ativa;
    if (filtro === 'arquivadas') return !m.ativa;
    return true;
  });

  const formatarData = (dataStr) => {
    if (!dataStr) return '—';
    return new Date(dataStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const renderQuadrante = (numero) => {
    const config = quadrantesConfig[numero];
    const colaboradoresQuadrante = posicoes.filter(p => p.quadrante === numero);

    return (
      <div
        key={numero}
        className={`${config.cor} border-2 rounded-lg p-3 min-h-[120px]`}
      >
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-medium text-gray-700">{config.nome}</p>
          <Badge variant="outline" className="text-xs">
            {colaboradoresQuadrante.length}
          </Badge>
        </div>

        <div className="space-y-1">
          {colaboradoresQuadrante.map(colab => (
            <div
              key={colab.id}
              className="bg-white border border-gray-200 rounded px-2 py-1 text-xs"
            >
              <p className="font-medium truncate">{colab.colaborador_nome}</p>
              <p className="text-[10px] text-gray-500">
                P: {colab.score_performance?.toFixed(1)} | Pot: {colab.score_potencial?.toFixed(1)}
              </p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="p-6 space-y-6">
      <Tabs value={abaAtiva} onValueChange={setAbaAtiva}>
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold flex items-center space-x-3">
              <LayoutGrid className="h-8 w-8 text-purple-600" />
              <span>Gerenciar Matrizes 9Box</span>
            </h1>
            <p className="text-gray-600 mt-1">
              Visualize, edite e administre todas as matrizes de avaliação de talentos
            </p>
          </div>
          <div className="flex items-center space-x-2">
            {abaAtiva === 'visualizar' && (
              <Button onClick={() => setAbaAtiva('gerenciar')} variant="outline">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Voltar
              </Button>
            )}
            <Button onClick={loadMatrizes} variant="outline">
              <RefreshCw className="h-4 w-4 mr-2" />
              Atualizar
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <TabsList>
          <TabsTrigger value="gerenciar">Gerenciar Matrizes</TabsTrigger>
          <TabsTrigger value="visualizar" disabled={!matrizVisualizando}>
            {matrizVisualizando ? `Visualizando: ${matrizVisualizando.nome}` : 'Visualizar Matriz'}
          </TabsTrigger>
        </TabsList>

        {/* Aba: Gerenciar */}
        <TabsContent value="gerenciar" className="space-y-6">
          {/* Estatísticas Gerais */}
          {estatisticasGerais && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Total de Matrizes</p>
                      <p className="text-3xl font-bold text-purple-600">
                        {estatisticasGerais.total_matrizes || 0}
                      </p>
                    </div>
                    <LayoutGrid className="h-10 w-10 text-purple-200" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Matrizes Ativas</p>
                      <p className="text-3xl font-bold text-green-600">
                        {estatisticasGerais.matrizes_ativas || 0}
                      </p>
                    </div>
                    <CheckCircle className="h-10 w-10 text-green-200" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Colaboradores Mapeados</p>
                      <p className="text-3xl font-bold text-blue-600">
                        {estatisticasGerais.total_colaboradores || 0}
                      </p>
                    </div>
                    <Users className="h-10 w-10 text-blue-200" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Sessões de Calibração</p>
                      <p className="text-3xl font-bold text-orange-600">
                        {estatisticasGerais.total_sessoes || 0}
                      </p>
                    </div>
                    <Target className="h-10 w-10 text-orange-200" />
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Filtros */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <Filter className="h-4 w-4 text-gray-500" />
                <span className="text-sm font-medium mr-2">Filtrar:</span>
                <Button
                  size="sm"
                  variant={filtro === 'todas' ? 'default' : 'outline'}
                  onClick={() => setFiltro('todas')}
                >
                  Todas ({matrizes.length})
                </Button>
                <Button
                  size="sm"
                  variant={filtro === 'ativas' ? 'default' : 'outline'}
                  onClick={() => setFiltro('ativas')}
                >
                  Ativas ({matrizes.filter(m => m.ativa).length})
                </Button>
                <Button
                  size="sm"
                  variant={filtro === 'arquivadas' ? 'default' : 'outline'}
                  onClick={() => setFiltro('arquivadas')}
                >
                  Arquivadas ({matrizes.filter(m => !m.ativa).length})
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Lista de Matrizes */}
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-2 text-purple-600" />
                <p className="text-gray-500">Carregando matrizes...</p>
              </div>
            </div>
          ) : matrizesParaExibir.length === 0 ? (
            <Card>
              <CardContent className="p-12">
                <div className="text-center">
                  <LayoutGrid className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">
                    {filtro === 'todas' ? 'Nenhuma matriz criada' : `Nenhuma matriz ${filtro}`}
                  </h3>
                  <p className="text-gray-500 mb-4">
                    {filtro === 'todas' 
                      ? 'Vá para a aba "Matriz Visual 9Box" → "Nova Matriz 9Box" para criar sua primeira matriz.'
                      : `Não há matrizes ${filtro} no momento.`
                    }
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {matrizesParaExibir.map((matriz) => (
                <Card 
                  key={matriz.id} 
                  className={`${matriz.ativa ? 'border-l-4 border-l-green-500' : 'border-l-4 border-l-gray-300'}`}
                >
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <CardTitle className="text-xl">{matriz.nome}</CardTitle>
                          <Badge variant={matriz.ativa ? 'default' : 'secondary'}>
                            {matriz.ativa ? (
                              <>
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Ativa
                              </>
                            ) : (
                              <>
                                <Archive className="h-3 w-3 mr-1" />
                                Arquivada
                              </>
                            )}
                          </Badge>
                        </div>
                        {matriz.descricao && (
                          <CardDescription>{matriz.descricao}</CardDescription>
                        )}
                      </div>
                    </div>
                  </CardHeader>

                  <CardContent className="space-y-4">
                    {/* Informações Principais */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg">
                      <div className="text-center">
                        <Users className="h-5 w-5 mx-auto mb-1 text-blue-600" />
                        <p className="text-2xl font-bold text-blue-600">
                          {matriz.total_colaboradores || 0}
                        </p>
                        <p className="text-xs text-gray-600">Colaboradores</p>
                      </div>
                      
                      <div className="text-center">
                        <Target className="h-5 w-5 mx-auto mb-1 text-purple-600" />
                        <p className="text-2xl font-bold text-purple-600">
                          {matriz.total_sessoes_calibracao || 0}
                        </p>
                        <p className="text-xs text-gray-600">Sessões</p>
                      </div>
                      
                      <div className="text-center">
                        <TrendingUp className="h-5 w-5 mx-auto mb-1 text-green-600" />
                        <p className="text-2xl font-bold text-green-600">
                          {matriz.total_ajustes || 0}
                        </p>
                        <p className="text-xs text-gray-600">Ajustes</p>
                      </div>
                      
                      <div className="text-center">
                        <Calendar className="h-5 w-5 mx-auto mb-1 text-orange-600" />
                        <p className="text-xs text-gray-600 mb-1">Período</p>
                        <p className="text-xs font-medium">
                          {formatarData(matriz.periodo_inicio)} até {formatarData(matriz.periodo_fim)}
                        </p>
                      </div>
                    </div>

                    {/* Informações Adicionais */}
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">Criada em:</span>{' '}
                        <span className="font-medium">{formatarData(matriz.criado_em)}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Última atualização:</span>{' '}
                        <span className="font-medium">{formatarData(matriz.atualizado_em)}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Peso Performance:</span>{' '}
                        <span className="font-medium">{matriz.peso_performance || 50}%</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Peso Potencial:</span>{' '}
                        <span className="font-medium">{matriz.peso_potencial || 50}%</span>
                      </div>
                    </div>

                    {/* Botões de Ação */}
                    <div className="flex items-center space-x-2 pt-2 border-t">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleVisualizar(matriz)}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        Visualizar
                      </Button>
                      
                      {matriz.ativa && (
                        <>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleExportar(matriz.id)}
                          >
                            <Download className="h-4 w-4 mr-1" />
                            Exportar
                          </Button>
                          
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleArquivar(matriz.id)}
                          >
                            <Archive className="h-4 w-4 mr-1" />
                            Arquivar
                          </Button>
                        </>
                      )}
                      
                      {!matriz.ativa && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleReativar(matriz.id)}
                        >
                          <RefreshCw className="h-4 w-4 mr-1" />
                          Reativar
                        </Button>
                      )}
                      
                      {/* Botão Excluir (sempre disponível) */}
                      <Button 
                        size="sm" 
                        variant="destructive"
                        onClick={() => handleExcluir(matriz.id)}
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Excluir
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Aba: Visualizar Matriz */}
        <TabsContent value="visualizar" className="space-y-6">
          {matrizVisualizando && (
            <>
              {/* Header da Matriz */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-2xl flex items-center space-x-2">
                        <span>{matrizVisualizando.nome}</span>
                        <Badge variant={matrizVisualizando.ativa ? 'default' : 'secondary'}>
                          {matrizVisualizando.ativa ? '✅ Ativa' : '📦 Arquivada'}
                        </Badge>
                      </CardTitle>
                      {matrizVisualizando.descricao && (
                        <CardDescription className="mt-2">{matrizVisualizando.descricao}</CardDescription>
                      )}
                    </div>
                  </div>
                </CardHeader>
              </Card>

              {/* Matriz Visual 9Box */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Matriz Visual 9Box</CardTitle>
                  <CardDescription>
                    {posicoes.length} colaboradores mapeados nesta matriz
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="flex items-center justify-center h-64">
                      <RefreshCw className="h-8 w-8 animate-spin text-purple-600" />
                    </div>
                  ) : posicoes.length === 0 ? (
                    <div className="text-center py-12">
                      <AlertCircle className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                      <p className="text-gray-500">Nenhum colaborador posicionado nesta matriz</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 gap-3">
                      {/* Linha 3 (Alto Potencial) */}
                      {renderQuadrante(7)}
                      {renderQuadrante(8)}
                      {renderQuadrante(9)}
                      
                      {/* Linha 2 (Médio Potencial) */}
                      {renderQuadrante(4)}
                      {renderQuadrante(5)}
                      {renderQuadrante(6)}
                      
                      {/* Linha 1 (Baixo Potencial) */}
                      {renderQuadrante(1)}
                      {renderQuadrante(2)}
                      {renderQuadrante(3)}
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>
      <ConfirmDialog />
    </div>
  );
};

export default GerenciarMatrizes9Box;
